"use client";

import {
  ChevronDown,
  Maximize2,
  NotebookPen,
  Plus,
  ReceiptText,
  UserRound,
} from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import type { ChatMessage, ComposerMode } from "@/app/ai-agent/types";
import aiAgentIcon from "@/assets/images/AI-Agent.png";
import ChatGlow from "@/assets/images/ai-chat-glow.svg";
import SparkleIcon from "@/assets/images/cashless-ai-v2.svg";
import { useAiChat } from "@/hooks/ai-agent/useAiChat";
import { useStickToBottom } from "@/hooks/ai-agent/useStickToBottom";
import { AiAgentHero } from "./AiAgentHero";
import { AiChatInput } from "./AiChatInput";
import { AiMessage } from "./AiMessage";
import { MAX_CHARS } from "./data";

/** Quick-action suggestion cards (Figma 213:220459). */
const CARDS = [
  { icon: UserRound, label: "Extract active lead data" },
  { icon: NotebookPen, label: "Summarize upcoming BEOs" },
  { icon: ReceiptText, label: "Fetch pending invoices" },
] as const;

export interface AskAiPopoverProps {
  /** Signed-in user's first name for the greeting. */
  name: string;
  onClose: () => void;
}

/**
 * The "Ask AI" popover (Figma 213:220459) opened from the top-nav — a fully
 * functional compact AI chat. It drives the SAME `useAiChat` hook (Redux-backed,
 * streaming) as the full `/ai-agent` page, so prompts are answered inline in the
 * popover with live streaming. Empty state shows the greeting + quick-action
 * cards (which send on click); once a turn starts, the message thread takes over.
 * "Expand" hands the conversation off to the full page; voice mode lives there.
 */
export function AskAiPopover({ name, onClose }: AskAiPopoverProps) {
  const router = useRouter();
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<ComposerMode>("text");

  const onTranscript = useCallback((text: string) => {
    setInput(text.slice(0, MAX_CHARS));
  }, []);
  const onDictationEnded = useCallback(() => setMode("text"), []);

  const {
    messages,
    turnStatus,
    toolActivity,
    sendMessage,
    cancelTurn,
    startDictation,
    stopDictation,
    newConversation,
  } = useAiChat({ onTranscript, onDictationEnded });

  const { scrollRef, contentRef, scrollToBottom } = useStickToBottom();

  const isStreaming = turnStatus !== "idle";
  const dictating = mode === "dictation";
  const hasThread = messages.length > 0;

  // Send the composer text (or a card prompt) as a turn — answered inline.
  const submit = (text: string) => {
    const content = text.trim();
    if (!content || isStreaming || dictating) return;
    setInput("");
    void sendMessage(content);
    scrollToBottom("smooth");
  };

  const handleStartDictation = () => {
    setMode("dictation");
    void startDictation().then((ok) => {
      if (!ok) setMode("text");
    });
  };
  const handleStopCapture = () => {
    if (dictating) void stopDictation();
    setMode("text");
  };

  // Hand the (shared, Redux-backed) conversation off to the full page.
  const expand = () => {
    void stopDictation();
    onClose();
    router.push("/ai-agent");
  };

  const startNewChat = () => {
    void stopDictation();
    newConversation();
    setInput("");
    setMode("text");
  };

  const editMessage = (m: ChatMessage) => setInput(m.content);
  const regenerate = (m: ChatMessage) => {
    if (!isStreaming) submit(m.content);
  };

  return (
    <div className="relative flex h-[80vh] max-h-[826px] w-[478px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-stroke-soft-200 bg-neutral-50 shadow-[0px_0px_2px_rgba(145,158,171,0.24),-20px_20px_40px_-4px_rgba(145,158,171,0.24)]">
      {/* Ambient blue glow at the bottom, as on the full agent page (213:201838). */}
      <ChatGlow className="pointer-events-none absolute inset-x-0 bottom-0 h-auto w-full" />

      {/* Header — brand icon (matches the top-nav Ask AI button) + actions. */}
      <div className="relative z-10 flex items-center justify-between px-4 py-3">
        <Image
          src={aiAgentIcon}
          alt=""
          width={24}
          height={24}
          className="size-6"
        />
        <div className="flex items-center gap-1">
          {hasThread && (
            <button
              type="button"
              onClick={startNewChat}
              aria-label="New chat"
              title="New chat"
              className="flex size-9 items-center justify-center rounded-[10px] text-text-tertiary transition-colors hover:bg-neutral-100"
            >
              <Plus className="size-5" />
            </button>
          )}
          <button
            type="button"
            onClick={expand}
            aria-label="Open full page"
            title="Open full page"
            className="flex size-9 items-center justify-center rounded-[10px] text-text-tertiary transition-colors hover:bg-neutral-100"
          >
            <Maximize2 className="size-[18px]" />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Collapse"
            title="Collapse"
            className="flex size-9 items-center justify-center rounded-[10px] text-text-tertiary transition-colors hover:bg-neutral-100"
          >
            <ChevronDown className="size-5" />
          </button>
        </div>
      </div>

      {/* Body: greeting + cards until the first turn, then the message thread. */}
      {hasThread ? (
        <div
          ref={scrollRef}
          className="relative z-10 flex-1 overflow-y-auto px-4"
        >
          <div ref={contentRef} className="flex flex-col gap-5 py-2">
            {messages.map((m) => (
              <AiMessage
                key={m.id}
                message={m}
                onEdit={editMessage}
                onRegenerate={regenerate}
                busy={isStreaming}
              />
            ))}
            {toolActivity && (
              <div className="flex items-center gap-2 pl-7 paragraph-sm text-text-tertiary">
                <SparkleIcon className="size-4 animate-pulse text-primary-500" />
                {toolActivity.label}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="relative z-10 flex flex-1 flex-col items-center justify-center gap-8 px-4">
          <AiAgentHero name={name} size="sm" />
          <div className="flex w-full gap-3">
            {CARDS.map(({ icon: Icon, label }) => (
              <button
                key={label}
                type="button"
                onClick={() => submit(label)}
                className="flex flex-1 flex-col gap-3 rounded-xl border border-stroke-soft-200 bg-white p-3.5 text-left transition-colors hover:bg-bg-weak-50"
              >
                <Icon className="size-7 text-primary-500" strokeWidth={1.5} />
                <span className="label-sm text-text-primary">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Composer — inline send + dictation (voice mode lives on the full page). */}
      <div className="relative z-10 p-4">
        <AiChatInput
          value={input}
          onChange={setInput}
          onSend={() => submit(input)}
          mode={mode}
          onStartDictation={handleStartDictation}
          onStartVoice={expand}
          onStop={handleStopCapture}
          maxChars={MAX_CHARS}
          showVoiceButton={false}
          isStreaming={isStreaming}
          onCancelTurn={cancelTurn}
        />
      </div>
    </div>
  );
}
